import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LSelect, SelectOption } from "@components/LSelect";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { updateCurrentLocation, updateLocations } from "@store/locationsSlice";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NavItems } from "../../../common/helpers/navigation";
import { AdminAsideNavIconContainer } from "./adminAsideNavIcon";
import { apiOrganizationToOrganization } from "@helpers/organizationHelper";
import { useApi } from "@services/useApi";
import { updateOrganization } from "@store/organizationSlice";
import { OrganizationAssessments, updateOrganizationAssessments } from "@store/organizationAssessmentsSlice";

export const AdminAsideNav = (): JSX.Element => {
    const { pathname } = useLocation();
    const validateMethods = useForm();
    const navigate = useNavigate();
    const { getOrganization, getOrganizationLocations, getAvailableAssessments } = useApi();

    const dispatch = useAppDispatch();
    const { locations, currentLocation, organization, userDetail, organizationAssessments } = useAppSelector((x) => {
        return {
            locations: x.orgLocations.locations,
            currentLocation: x.orgLocations.currentLocation,
            organization: x.organization,
            userDetail: x.userDetail,
            organizationAssessments: x.organizationAssessments
        };
    });
    const { isClinicalAdmin, isFacilitator, isClinician, isDataQa, isResearcher, isSuperAdmin } = useUserPermission({
        useCurrentLocation: true,
        locationId: currentLocation?.id
    });

    const [selectedOrganization, setSelectedOrganization] = useState(
        isEmpty(userDetail.organizations) ? "0" : organization.id!.toString()
    );
    const [collapsed, setCollapsed] = useState(false);
    const containerClasses = classNames(
        "column",
        "is-hidden-mobile",
        "admin-left-nav-container",
        collapsed ? "is-collapsed" : ""
    );

    const locationOptions = userDetail.locations
        .filter((x) => x.organizationId.toString() === selectedOrganization)
        .map((x) => {
            return { name: x.name, value: x.id.toString() } as SelectOption;
        });

    useEffect(() => {
        if (organization?.id) {
            setSelectedOrganization(organization.id.toString());

            void loadAvailablePurchases(organization?.id);
        }
    }, [organization]);

    const loadAvailablePurchases = async (orgId: number): Promise<void> => {
        const data = await getAvailableAssessments(orgId);
        dispatch(updateOrganizationAssessments(data as OrganizationAssessments));
    };

    const handleLocationSelect = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const locationSelected = locations.find((x) => x.id.toString() === e.target.value);

        dispatch(updateCurrentLocation(locationSelected));
    };

    const handleOrganizationSelect = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        navigate(NavItems.Admin.Manage.Overview.path);

        const orgId = Number(e.target.value);
        setSelectedOrganization(orgId.toString());
        dispatch(updateCurrentLocation(undefined));
        dispatch(updateLocations([]));

        void loadAvailablePurchases(orgId);

        const myOrganization = await getOrganization(orgId);
        dispatch(updateOrganization(apiOrganizationToOrganization(myOrganization)));

        const myLocations = await getOrganizationLocations(orgId);
        dispatch(updateLocations(myLocations));

        dispatch(updateCurrentLocation(myLocations[0]));
    };

    const getOrganizations = (): SelectOption[] => {
        return userDetail.organizations.map((x) => {
            return { name: x.name, value: x.id.toString() } as SelectOption;
        });
    };

    const renderLinks = (): JSX.Element => {
        const items: any[] = [
            {
                title: "Assessments",
                to: NavItems.Admin.Assessments.Upcoming.path,
                includePath: NavItems.Admin.Assessments.parentRoot,
                icon: <FontAwesomeIcon icon={solid("file")} />,
                show: isClinicalAdmin || isFacilitator,
                disabled: !organizationAssessments.hasPurchases
            },
            {
                title: "Results",
                to: NavItems.Admin.Results.parentRoot.path,
                includePath: NavItems.Admin.Results.parentRoot.path,
                icon: <FontAwesomeIcon icon={solid("chart-line")} />,
                show: isClinician || isDataQa || isResearcher,
                disabled: !organizationAssessments.hasPurchases
            },
            {
                title: "Manage",
                to: NavItems.Admin.Manage.Overview.path,
                includePath: NavItems.Admin.Manage.parentRoot,
                icon: <FontAwesomeIcon icon={solid("gear")} />,
                show: isClinicalAdmin || isFacilitator,
                disabled: false
            },
            {
                title: "Super Admin",
                to: NavItems.SuperAdmin.Organizations.path,
                includePath: NavItems.SuperAdmin.Landing.path,
                icon: <FontAwesomeIcon icon={solid("user-plus")} />,
                show: isSuperAdmin,
                disabled: false
            }
        ];

        return (
            <ul className="menu-list">
                {items.map((x, i) => {
                    return (
                        x.show && (
                            <li key={`admin-aside-nav-item-${i}`}>
                                <Link
                                    to={x.disabled ? "#" : x.to}
                                    className={`${pathname.includes(x.includePath) ? "is-active" : ""} ${
                                        x.disabled || !organization.id || !currentLocation?.id ? "disabled-link" : ""
                                    }`}
                                >
                                    <AdminAsideNavIconContainer>{x.icon}</AdminAsideNavIconContainer>
                                    <span className="hide-when-small">{x.title}</span>
                                </Link>
                            </li>
                        )
                    );
                })}
            </ul>
        );
    };

    return (
        <div className={containerClasses}>
            <div className="pt-6 pl-4">
                <a href="#" onClick={() => setCollapsed(!collapsed)}>
                    {collapsed && <FontAwesomeIcon className="is-color-light" icon={solid("bars")} size={"xl"} />}
                    {!collapsed && <FontAwesomeIcon className="is-color-light" icon={solid("less-than")} size={"xl"} />}
                </a>
            </div>
            <aside className="menu pt-7">
                <div className="is-flex is-flex-direction-column is-justify-content-center hide-when-small px-4">
                    <FormProvider {...validateMethods}>
                        <LSelect
                            theme="dark"
                            options={getOrganizations()}
                            label={"Organizations"}
                            inputName="org"
                            onChange={handleOrganizationSelect}
                            value={selectedOrganization}
                            disabled={!organization.id}
                            isFullWidth
                        />
                        {!isEmpty(locationOptions) && (
                            <LSelect
                                theme="dark"
                                options={locationOptions}
                                label={"Locations"}
                                inputName="loc"
                                onChange={handleLocationSelect}
                                value={currentLocation?.id.toString()}
                                disabled={!organization.id}
                                isFullWidth
                            />
                        )}
                    </FormProvider>
                </div>
                {renderLinks()}
            </aside>
        </div>
    );
};
